import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { toast } from 'sonner';
import { isDateToday, getTodayStringInOfficeTimezone } from '@/lib/timezoneUtils';
import { OFFICE_TIMEZONE } from '@/locales';

export type AutoChargePeriod = 'today' | 'this_week' | 'this_month' | 'all';
export type AutoChargeStatusFilter = 'all' | 'success' | 'retry_pending' | 'exhausted' | 'no_card';

export interface AutoChargePayment {
  id: string;
  deal_id: string;
  amount: number;
  paid_amount: number | null;
  due_date: string;
  status: string;
  auto_charge_enabled: boolean;
  auto_charge_status: string | null;
  auto_charge_attempts: number | null;
  last_charge_attempt_at: string | null;
  last_charge_error: string | null;
  installment_number: number | null;
  description: string | null;
  deal: {
    id: string;
    name: string;
    contact_id: string;
    contact: {
      id: string;
      first_name: string | null;
      last_name: string | null;
      stripe_customer_id: string | null;
      stripe_default_payment_method_id: string | null;
      has_payment_method: boolean | null;
    };
  };
}

export interface AutoChargeSummary {
  total_today: number;
  total_amount_today: number; // Total original amount (base for all % calculations)
  success_count: number;
  success_amount: number;
  success_rate: number;
  failed_retry_pending: number;
  failed_retry_pending_amount: number;
  exhausted_retries: number;
  exhausted_retries_amount: number;
  no_card: number;
  no_card_amount: number;
}

interface UseAutoChargePaymentsOptions {
  period?: AutoChargePeriod;
  statusFilter?: AutoChargeStatusFilter;
}

function getPeriodRange(period: AutoChargePeriod): { start: string; end: string } | null {
  // Use Orlando timezone for business date calculations
  const todayStr = getTodayStringInOfficeTimezone(); // "2025-12-31" in Orlando
  const todayDate = new Date(); // For week/month calculations

  switch (period) {
    case 'today': {
      return { start: todayStr, end: todayStr };
    }
    case 'this_week': {
      const weekStart = startOfWeek(todayDate, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(todayDate, { weekStartsOn: 1 });
      return { 
        start: formatInTimeZone(weekStart, OFFICE_TIMEZONE, 'yyyy-MM-dd'), 
        end: formatInTimeZone(weekEnd, OFFICE_TIMEZONE, 'yyyy-MM-dd') 
      };
    }
    case 'this_month': {
      const monthStart = startOfMonth(todayDate);
      const monthEnd = endOfMonth(todayDate);
      return { 
        start: formatInTimeZone(monthStart, OFFICE_TIMEZONE, 'yyyy-MM-dd'), 
        end: formatInTimeZone(monthEnd, OFFICE_TIMEZONE, 'yyyy-MM-dd') 
      };
    }
    case 'all':
    default:
      return null;
  }
}

export function useAutoChargePayments({
  period = 'today',
  statusFilter = 'all'
}: UseAutoChargePaymentsOptions = {}) {
  return useQuery({
    queryKey: ['auto-charge-payments', period, statusFilter],
    queryFn: async () => {
      // Build query for payments with auto_charge_enabled AND saved card
      // Only show payments where contact has a valid payment method
      let query = supabase
        .from('payments')
        .select(`
          id,
          deal_id,
          amount,
          paid_amount,
          due_date,
          status,
          auto_charge_enabled,
          auto_charge_status,
          auto_charge_attempts,
          last_charge_attempt_at,
          last_charge_error,
          installment_number,
          description,
          deals!inner (
            id,
            name,
            contact_id,
            contacts!fk_deals_contact!inner (
              id,
              first_name,
              last_name,
              stripe_customer_id,
              stripe_default_payment_method_id,
              has_payment_method
            )
          )
        `)
        .eq('auto_charge_enabled', true)
        .not('status', 'eq', 'cancelled')
        .not('deals.contacts.stripe_default_payment_method_id', 'is', null)
        .order('due_date', { ascending: true });

      // Apply period filter
      const periodRange = getPeriodRange(period);
      if (periodRange) {
        query = query.gte('due_date', periodRange.start).lte('due_date', periodRange.end);
      }

      // Note: Status filter is applied client-side after bucketization to match card categories

      const { data, error } = await query;

      if (error) throw error;

      // Transform data
      const payments: AutoChargePayment[] = (data || []).map((p: any) => ({
        id: p.id,
        deal_id: p.deal_id,
        amount: p.amount,
        paid_amount: p.paid_amount,
        due_date: p.due_date,
        status: p.status,
        auto_charge_enabled: p.auto_charge_enabled,
        auto_charge_status: p.auto_charge_status,
        auto_charge_attempts: p.auto_charge_attempts,
        last_charge_attempt_at: p.last_charge_attempt_at,
        last_charge_error: p.last_charge_error,
        installment_number: p.installment_number,
        description: p.description,
        deal: {
          id: p.deals.id,
          name: p.deals.name,
          contact_id: p.deals.contact_id,
          contact: p.deals.contacts
        }
      }));

      // Calculate summary - filter today payments with timezone-safe check
      const todayPayments = payments.filter(p => isDateToday(p.due_date));

      // Helpers for amount calculations
      const calcRemainingAmount = (list: AutoChargePayment[]) => 
        list.reduce((sum, p) => sum + (p.amount - (p.paid_amount || 0)), 0);

      const calcPaidAmount = (list: AutoChargePayment[]) => 
        list.reduce((sum, p) => sum + (p.paid_amount || 0), 0);

      const calcOriginalAmount = (list: AutoChargePayment[]) => 
        list.reduce((sum, p) => sum + p.amount, 0);

      // Bucketize today's payments with priority (each payment in exactly one bucket)
      const todaySuccess: AutoChargePayment[] = [];
      const todayNoCard: AutoChargePayment[] = [];
      const todayExhausted: AutoChargePayment[] = [];
      const todayRetryPending: AutoChargePayment[] = [];

      for (const payment of todayPayments) {
        const attempts = payment.auto_charge_attempts || 0;
        const status = payment.auto_charge_status;
        const contact = payment.deal.contact;
        const hasPaymentMethod = contact?.stripe_default_payment_method_id || contact?.has_payment_method;

        // Priority 1: Success (paid or charged)
        if (payment.status === 'paid' || status === 'charged') {
          todaySuccess.push(payment);
        }
        // Priority 2: No card (no payment method)
        else if (status === 'no_card' || !hasPaymentMethod) {
          todayNoCard.push(payment);
        }
        // Priority 3: Exhausted retries (failed with 3+ attempts)
        else if (status === 'failed' && attempts >= 3) {
          todayExhausted.push(payment);
        }
        // Priority 4: Retry pending (everything else - pending, processing, failed with < 3 attempts)
        else {
          todayRetryPending.push(payment);
        }
      }

      // Total original amount for today (base for all percentages)
      const totalAmountToday = calcOriginalAmount(todayPayments);

      const summary: AutoChargeSummary = {
        total_today: todayPayments.length,
        total_amount_today: totalAmountToday,
        success_count: todaySuccess.length,
        success_amount: calcPaidAmount(todaySuccess),
        success_rate: todayPayments.length > 0 ? (todaySuccess.length / todayPayments.length) * 100 : 0,
        failed_retry_pending: todayRetryPending.length,
        failed_retry_pending_amount: calcRemainingAmount(todayRetryPending),
        exhausted_retries: todayExhausted.length,
        exhausted_retries_amount: calcRemainingAmount(todayExhausted),
        no_card: todayNoCard.length,
        no_card_amount: calcRemainingAmount(todayNoCard)
      };

      // Apply client-side status filter based on buckets (for all payments, not just today)
      let filteredPayments = payments;
      if (statusFilter !== 'all') {
        // Bucketize all payments using the same logic
        const bucketize = (payment: AutoChargePayment) => {
          const attempts = payment.auto_charge_attempts || 0;
          const status = payment.auto_charge_status;
          const contact = payment.deal.contact;
          const hasPaymentMethod = contact?.stripe_default_payment_method_id || contact?.has_payment_method;

          if (payment.status === 'paid' || status === 'charged') return 'success';
          if (status === 'no_card' || !hasPaymentMethod) return 'no_card';
          if (status === 'failed' && attempts >= 3) return 'exhausted';
          return 'retry_pending';
        };

        filteredPayments = payments.filter(p => bucketize(p) === statusFilter);
      }

      return { payments: filteredPayments, summary };
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  });
}

export interface ChargeResult {
  success: boolean;
  error?: string;
  amount_charged?: number;
  no_balance?: boolean;
}

export function useChargePaymentNow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string): Promise<ChargeResult> => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-charge-saved-card', {
        body: { 
          payment_id: paymentId, 
          force: true,
          triggered_by: user.id,
          trigger_type: 'manual'
        }
      });

      if (error) {
        return { success: false, error: error.message };
      }
      
      if (!data?.success) {
        return { 
          success: false, 
          error: data?.error || data?.message || 'Charge failed',
          no_balance: data?.no_balance
        };
      }

      return { 
        success: true, 
        amount_charged: data?.amount_charged 
      };
    },
    onSuccess: (result) => {
      if (result.success) {
        queryClient.invalidateQueries({ queryKey: ['auto-charge-payments'] });
        queryClient.invalidateQueries({ queryKey: ['charge-history'] });
        queryClient.invalidateQueries({ queryKey: ['payment-transactions'] });
      }
    }
  });
}

export function useDisableAutoCharge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (paymentId: string) => {
      const { error } = await supabase
        .from('payments')
        .update({ 
          auto_charge_enabled: false,
          auto_charge_status: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', paymentId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast.success('Cobrança automática desativada');
      queryClient.invalidateQueries({ queryKey: ['auto-charge-payments'] });
    },
    onError: (error: any) => {
      toast.error(`Erro: ${error.message}`);
    }
  });
}

export function useCreatePaymentLink() {
  return useMutation({
    mutationFn: async ({ contactId, paymentId, amount }: { contactId: string; paymentId: string; amount: number }) => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const { data, error } = await supabase.functions.invoke('stripe-create-payment-link', {
        body: {
          contact_id: contactId,
          payment_id: paymentId,
          amount,
          origin_context: 'finance_auto_charge_fallback',
          mode: 'payment',
          success_url: window.location.href,
          cancel_url: window.location.href
        }
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) {
        window.open(data.url, '_blank');
        toast.success('Link de pagamento gerado');
      }
    },
    onError: (error: any) => {
      toast.error(`Erro ao gerar link: ${error.message}`);
    }
  });
}
